import React from 'react';
import Icon from '../../../components/AppIcon';

const FairInfoSection = () => {
  const features = [
    {
      icon: 'Video',
      title: 'Live Video Counseling',
      description: 'Connect face-to-face with admission counselors through HD video sessions. Get personalized guidance and instant answers to your questions.',
      gradient: 'from-red-500 to-pink-500',
      bgColor: 'from-red-50 to-pink-50'
    },
    {
      icon: 'Compass',
      title: '360° Campus Tours',
      description: 'Explore campuses virtually with immersive 360-degree tours. Visit classrooms, labs, libraries, and facilities from anywhere.',
      gradient: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-50 to-cyan-50'
    },
    {
      icon: 'MessageSquare',
      title: 'Instant Chat Support',
      description: 'Get real-time responses from institute representatives. Ask questions, request brochures, and schedule appointments instantly.',
      gradient: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-50 to-emerald-50'
    },
    {
      icon: 'Download',
      title: 'Downloadable Resources',
      description: 'Access course catalogs, admission forms, fee structures, and scholarship information. Download and save for future reference.',
      gradient: 'from-purple-500 to-violet-500',
      bgColor: 'from-purple-50 to-violet-50'
    },
    {
      icon: 'Users',
      title: 'Virtual Booths',
      description: 'Visit interactive institute booths with videos, presentations, and detailed information about programs and facilities.',
      gradient: 'from-orange-500 to-amber-500',
      bgColor: 'from-orange-50 to-amber-50'
    },
    {
      icon: 'Award',
      title: 'Scholarship Information',
      description: 'Discover scholarship opportunities, eligibility criteria, and application processes. Find financial aid options that suit you.',
      gradient: 'from-indigo-500 to-blue-500',
      bgColor: 'from-indigo-50 to-blue-50'
    }
  ];

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-5 py-2 rounded-full mb-4 shadow-md">
            <Icon name="Sparkles" size={16} color="white" />
            <span className="text-sm font-medium">Virtual Education Fair</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            How StudyExpo Helps You
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Experience the future of education exploration with our comprehensive virtual fair platform. Connect, explore, and make informed decisions about your academic future.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features?.map((feature, index) => (
            <div 
              key={index}
              className={`bg-gradient-to-br ${feature?.bgColor} border-2 border-white rounded-2xl p-6 hover:shadow-2xl transition-all hover:-translate-y-2`}
            >
              <div className={`w-14 h-14 bg-gradient-to-br ${feature?.gradient} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                <Icon name={feature?.icon} size={24} color="white" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                {feature?.title}
              </h3>
              <p className="text-muted-foreground">
                {feature?.description}
              </p>
            </div>
          ))}
        </div>

        {/* Why Virtual Fair Section */}
        <div className="mt-16 bg-gradient-to-br from-amber-100 via-orange-100 to-pink-100 rounded-2xl p-8 md:p-12 border-2 border-orange-200 shadow-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
                Why Choose a Virtual Education Fair?
              </h3>
              <p className="text-muted-foreground mb-6 text-lg">
                Traditional education fairs require travel, time, and often overwhelming crowds. StudyExpo brings the entire experience to your screen with enhanced features and convenience.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                    <Icon name="Check" size={16} color="white" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Save Time & Money</p>
                    <p className="text-sm text-muted-foreground">No travel costs, no waiting in lines</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                    <Icon name="Check" size={16} color="white" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Access Anytime, Anywhere</p>
                    <p className="text-sm text-muted-foreground">Explore at your own pace, 24/7 availability</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                    <Icon name="Check" size={16} color="white" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">More Institutions</p>
                    <p className="text-sm text-muted-foreground">Connect with 500+ institutes in one place</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                    <Icon name="Check" size={16} color="white" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Better Information</p>
                    <p className="text-sm text-muted-foreground">Detailed resources, videos, and documentation</p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white border-2 border-cyan-200 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all">
                <p className="text-4xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent mb-2">Top Institutes</p>
                <p className="text-sm text-muted-foreground font-medium">Participating Institutes</p>
              </div>
              <div className="bg-white border-2 border-violet-200 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all">
                <p className="text-4xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent mb-2">100+</p>
                <p className="text-sm text-muted-foreground font-medium">Course Options</p>
              </div>
              <div className="bg-white border-2 border-amber-200 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all">
                <p className="text-4xl font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent mb-2">24/7</p>
                <p className="text-sm text-muted-foreground font-medium">Access Available</p>
              </div>
              <div className="bg-white border-2 border-green-200 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all">
                <p className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-2">100%</p>
                <p className="text-sm text-muted-foreground font-medium">Free to Join</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FairInfoSection;